// Quaquaversal curve, generated procedurally from the substitution rules of
// the Conway–Radin tiling. Depth d visits 8^d prisms; the drawing enters each
// leaf prism at a vertex, passes through its centroid and leaves at the exit
// vertex, so it has 2 * 8^d + 1 points. A direct port of the Curve Atlas
// page's generator (test/oracle/quaqua-reference.js), producing the same f64
// coordinates before the Float32 store.
import type { CurveGeometry } from '../types.ts';
import { QUAQUA_RULES, type QuaquaRules } from './quaqua-rules.ts';

export const QUAQUA_MAX_DEPTH = 7;   // 4.2 M points, the same ceiling as Hilbert order 11

export function quaquaPointCount(depth: number): number {
  return 2 * 8 ** depth + 1;
}

/**
 * The 8^depth leaf prisms as 3x4 affines (row-major: c00 c01 c02 t0, ...),
 * in traversal order, so prism i holds curve points 2i and 2i+1. Used by the
 * tiling overlay.
 */
export function buildQuaquaPrisms(depth: number, data: QuaquaRules = QUAQUA_RULES): Float64Array {
  depth = Math.max(1, Math.min(QUAQUA_MAX_DEPTH, depth | 0));
  const n = 8 ** depth;
  const out = new Float64Array(n * 12);
  let cursor = 0;
  const C: Float64Array[] = [], T: Float64Array[] = [];
  for (let l = 0; l <= depth; l++) { C.push(new Float64Array(9)); T.push(new Float64Array(3)); }
  const visit = (a: Float64Array | number[], b: Float64Array | number[], word: number[][], remaining: number) => {
    const c = C[remaining], t = T[remaining];
    for (const [child, entry, exit] of word) {
      const m = data.maps[child];
      for (let i = 0; i < 3; i++) {
        t[i] = a[3 * i] * m.b[0] + a[3 * i + 1] * m.b[1] + a[3 * i + 2] * m.b[2] + b[i];
        for (let j = 0; j < 3; j++) c[3 * i + j] = a[3 * i] * m.a[0][j] + a[3 * i + 1] * m.a[1][j] + a[3 * i + 2] * m.a[2][j];
      }
      if (remaining > 1) visit(c, t, data.rules[entry + ',' + exit], remaining - 1);
      else {
        for (let i = 0; i < 3; i++) { out[cursor++] = c[3 * i]; out[cursor++] = c[3 * i + 1]; out[cursor++] = c[3 * i + 2]; out[cursor++] = t[i]; }
      }
    }
  };
  visit([1, 0, 0, 0, 1, 0, 0, 0, 1], [0, 0, 0], data.root, depth);
  if (cursor !== n * 12) throw new Error('incomplete Quaquaversal prism tiling');
  return out;
}

export function buildQuaquaversal(depth: number, data: QuaquaRules = QUAQUA_RULES): CurveGeometry {
  depth = Math.max(1, Math.min(QUAQUA_MAX_DEPTH, depth | 0));
  const n = quaquaPointCount(depth);
  const coords = new Float32Array(n * 3);
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  let cursor = 0;
  const point = (a: Float64Array | number[], b: Float64Array | number[], v: number[]) => {
    for (let i = 0; i < 3; i++) {
      const x = a[3 * i] * v[0] + a[3 * i + 1] * v[1] + a[3 * i + 2] * v[2] + b[i];
      coords[cursor++] = x;
      if (x < min[i]) min[i] = x;
      if (x > max[i]) max[i] = x;
    }
  };
  let lastExit: number[] | null = null;
  // One affine (c, t) scratch pair per recursion level: the recursion is only
  // `depth` deep, so this avoids two allocations per visited prism.
  const C: Float64Array[] = [], T: Float64Array[] = [];
  for (let l = 0; l <= depth; l++) { C.push(new Float64Array(9)); T.push(new Float64Array(3)); }
  const visit = (a: Float64Array | number[], b: Float64Array | number[], word: number[][], remaining: number) => {
    const c = C[remaining], t = T[remaining];
    for (const [child, entry, exit] of word) {
      const m = data.maps[child];
      for (let i = 0; i < 3; i++) {
        t[i] = a[3 * i] * m.b[0] + a[3 * i + 1] * m.b[1] + a[3 * i + 2] * m.b[2] + b[i];
        for (let j = 0; j < 3; j++) c[3 * i + j] = a[3 * i] * m.a[0][j] + a[3 * i + 1] * m.a[1][j] + a[3 * i + 2] * m.a[2][j];
      }
      if (remaining > 1) visit(c, t, data.rules[entry + ',' + exit], remaining - 1);
      else {
        point(c, t, data.vertices[entry]);
        point(c, t, data.centroid);
        lastExit = data.vertices[exit];
      }
    }
  };
  visit([1, 0, 0, 0, 1, 0, 0, 0, 1], [0, 0, 0], data.root, depth);
  if (!lastExit) throw new Error('empty Quaquaversal traversal');
  point(C[1], T[1], lastExit);   // the level-1 scratch still holds the final leaf's affine
  if (cursor !== n * 3) throw new Error('incomplete Quaquaversal traversal');
  return { dims: 3, nPoints: n, closed: false, bbox: { min, max }, coords };
}
